import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { shipmentService } from '../../services/shipmentService'
import { validators, ValidationMessage } from '../common/Validation.js'

const GSTIN_LENGTH = 15
const gstLookupCache = new Map()

const GstVerificationField = ({
  name,
  value,
  onChange,
  onKeyDown,
  onStatusChange,
  disabled = false,
  error,
  enabled = false,
  placeholder = 'GST Number (Optional)',
  className = '',
}) => {
  const [lookup, setLookup] = useState({ status: 'idle', data: null, error: '' })
  const lastGstinRef = useRef('')
  const debounceRef = useRef(null)
  const requestIdRef = useRef(0)
  const onStatusChangeRef = useRef(onStatusChange)
  onStatusChangeRef.current = onStatusChange

  const handleInputChange = (e) => {
    const nextValue = String(e.target.value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, GSTIN_LENGTH)
    onChange({ target: { name, value: nextValue, type: 'text' } })
  }

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    if (!enabled) {
      lastGstinRef.current = ''
      setLookup({ status: 'idle', data: null, error: '' })
      return undefined
    }

    const gstin = String(value || '').trim().toUpperCase()
    if (gstin.length !== GSTIN_LENGTH || validators.gst(gstin)) {
      lastGstinRef.current = ''
      setLookup({ status: 'idle', data: null, error: '' })
      return undefined
    }

    if (gstin === lastGstinRef.current) return undefined

    const cached = gstLookupCache.get(gstin)
    if (cached) {
      lastGstinRef.current = gstin
      setLookup({ status: 'done', data: cached, error: '' })
      return undefined
    }

    debounceRef.current = setTimeout(async () => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      lastGstinRef.current = gstin
      setLookup({ status: 'loading', data: null, error: '' })
      try {
        const response = await shipmentService.searchGstin(gstin)
        if (requestIdRef.current !== requestId) return
        const data = response?.data?.data || {}
        gstLookupCache.set(gstin, data)
        setLookup({ status: 'done', data, error: '' })
      } catch (err) {
        if (requestIdRef.current !== requestId) return
        lastGstinRef.current = ''
        setLookup({
          status: 'error',
          data: null,
          error: err?.response?.data?.error || 'Unable to verify GST number',
        })
      }
    }, 400)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [enabled, value])

  useEffect(() => {
    const cb = onStatusChangeRef.current
    if (!cb) return
    const verifiedData = lookup.status === 'done' ? lookup.data : null
    cb({
      gstin: String(value || '').trim().toUpperCase(),
      lookup: lookup.status,
      valid: Boolean(verifiedData?.valid),
      active: Boolean(verifiedData?.active),
      error: lookup.error || '',
    })
  }, [lookup, value])

  const verified = lookup.status === 'done' && lookup.data
  const isActive = Boolean(verified?.active)
  const isInvalid = verified && !verified.valid
  const isInactive = verified && verified.valid && !verified.active

  let borderClass = error ? 'border-red-400' : 'border-gray-300'
  if (enabled && lookup.status === 'loading') borderClass = 'border-blue-400'
  if (enabled && isActive) borderClass = 'border-green-400'
  if (enabled && (isInvalid || isInactive || lookup.status === 'error')) borderClass = 'border-red-400'

  return (
    <div className="flex flex-col">
      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
        GST Number
      </label>
      <div className="relative">
        <input
          type="text"
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          maxLength={GSTIN_LENGTH}
          disabled={disabled}
          autoComplete="off"
          className={
            className ||
            `w-full px-3 sm:px-4 py-2 sm:py-2.5 ${enabled ? 'pr-10' : ''} text-sm sm:text-base border ${borderClass} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100`
          }
        />
        {enabled && lookup.status === 'loading' && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 animate-spin" />
        )}
        {enabled && isActive && (
          <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600" />
        )}
        {enabled && (isInvalid || isInactive || lookup.status === 'error') && (
          <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
        )}
      </div>
      <ValidationMessage error={error} />
      {enabled && lookup.status === 'loading' && (
        <p className="text-xs text-blue-600 mt-1">Checking GSTIN...</p>
      )}
      {enabled && isActive && (
        <p className="text-xs text-green-700 mt-1">
          Active{verified.display_name ? ` — ${verified.display_name}` : ''}
        </p>
      )}
      {enabled && isInactive && (
        <p className="text-xs text-red-600 mt-1">
          GSTIN is {verified.status || 'not Active'}
          {verified.display_name ? ` — ${verified.display_name}` : ''}
        </p>
      )}
      {enabled && isInvalid && (
        <p className="text-xs text-red-600 mt-1">
          {verified.error || 'GSTIN not found or invalid'}
        </p>
      )}
      {enabled && lookup.status === 'error' && (
        <p className="text-xs text-red-600 mt-1">{lookup.error}</p>
      )}
    </div>
  )
}

export default GstVerificationField
